import type { PayloadOf } from '@forgd/shared/realtime/realtime-channel.types'
import { useDevLogger } from '#core/composables/useDevLogger'
import { projectEditorChannel, projectEditorUserChannel } from '@forgd/shared/realtime/project-permissions-channels'
import { createChannel } from '@forgd/shared/realtime/realtime-channel'
import { defineStore } from 'pinia'
import { useProjectEditors } from './useProjectEditors'

export const useProjectEditorsRealtime = defineStore('projectEditorsRealtime', () => {
  const logger = useDevLogger('useProjectEditorsRealtime')
  const supabase = useSupabaseClient()
  const projectEditorsData = useProjectEditors()

  let unsubscribeFunctions: (() => void)[] = []

  // Event handlers that update the data store
  function handleEditorRequest({ pendingEditor, autoApproveAt }: PayloadOf<typeof projectEditorUserChannel, 'project.edit.requested'>) {
    logger.debug('Editor request received', { pendingEditor, autoApproveAt })
    projectEditorsData.setPendingEditor(pendingEditor)
    projectEditorsData.autoApproveAt = new Date(autoApproveAt)
    projectEditorsData.setEditRequestStatus('pending_request')
  }

  function handleEditorApproved(newActiveEditor: PayloadOf<typeof projectEditorUserChannel, 'project.edit.approved'>) {
    logger.debug('Editor approved', newActiveEditor)
    projectEditorsData.setActiveEditor(newActiveEditor)
    projectEditorsData.clearPendingEditor()
    projectEditorsData.autoApproveAt = null
    projectEditorsData.setEditRequestStatus('approved')
  }

  function handleEditorRejected() {
    logger.debug('Edit request rejected')
    projectEditorsData.clearPendingEditor()
    projectEditorsData.autoApproveAt = null
    projectEditorsData.setEditRequestStatus('denied')
  }

  function handleEditorUpdated(newActiveEditor: PayloadOf<typeof projectEditorChannel, 'project.edit.updated'>) {
    logger.debug('Editor updated', newActiveEditor)
    projectEditorsData.setActiveEditor(newActiveEditor)
    projectEditorsData.clearPendingEditor()
    projectEditorsData.autoApproveAt = null
    projectEditorsData.setEditRequestStatus('none')
  }

  async function subscribeToProject(projectId: string, userId: string) {
    await unsubscribe()
    logger.debug('[subscribeToProject] Subscribing to project', { projectId, userId })

    try {
      // User specific notifications
      const userChannel = createChannel(
        supabase,
        projectEditorUserChannel,
        { projectId, userId },
        logger.debug.bind(logger),
      )
      const unsubscribeUserRequested = userChannel.subscribe('project.edit.requested', handleEditorRequest)
      const unsubscribeUserApproved = userChannel.subscribe('project.edit.approved', handleEditorApproved)
      const unsubscribeUserRejected = userChannel.subscribe('project.edit.rejected', handleEditorRejected)

      // Project-wide notifications (updates that affect everyone)
      const projectChannel = createChannel(
        supabase,
        projectEditorChannel,
        { projectId },
        logger.debug.bind(logger),
      )
      const unsubscribeProjectUpdated = projectChannel.subscribe('project.edit.updated', handleEditorUpdated)

      // Store unsubscribe functions for cleanup
      unsubscribeFunctions = [
        unsubscribeUserRequested,
        unsubscribeUserApproved,
        unsubscribeUserRejected,
        unsubscribeProjectUpdated,
      ]
    }
    catch (err) {
      logger.error('[subscribeToProject] Failed to subscribe', err)
    }
  }

  async function unsubscribe() {
    if (unsubscribeFunctions.length === 0) {
      return
    }
    logger.debug('[unsubscribe] Unsubscribing from all editor notifications')

    // Call all unsubscribe functions
    unsubscribeFunctions.forEach(unsubscribe => unsubscribe())
    unsubscribeFunctions = []
  }

  // Note: Subscription lifecycle is managed by the route middleware
  // No auto-subscription here to avoid conflicts

  return {
    subscribeToProject,
    unsubscribe,
  }
})
